/**
 * En qué local estoy vendiendo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTO ES UN ESTADO DEL DISPOSITIVO Y NO DE LA CUENTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque la sucursal no es una propiedad de la persona: **es una propiedad de
 * la tablet**. La que está en el mostrador de la sucursal Norte vende siempre en
 * la Norte, la maneje quien la maneje. Si esto viajara en la cuenta, el dueño
 * que entra desde su casa a mirar los números le cambiaría el local a la caja
 * donde su empleado está cobrando.
 *
 * Por eso se guarda en el navegador y no en el servidor: se elige una vez al
 * montar el equipo, y no se vuelve a tocar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ SE GUARDA POR NEGOCIO, NO UNA SOLA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un dueño con tres negocios tiene sucursales distintas en cada uno, y los
 * identificadores de uno no existen en el otro. Con un solo valor guardado,
 * cambiar de negocio dejaría apuntando a una sucursal que ahí no existe — y la
 * venta se rechazaría con «Esa sucursal no existe», que es un error imposible
 * de entender para quien está cobrando.
 *
 * Guardándolo por negocio, cada uno recuerda el suyo y cambiar de negocio no
 * arrastra nada.
 */

import { create } from "zustand";

const LLAVE = "softfi-sucursal-por-negocio";

type Guardado = Record<string, string>;

function leer(): Guardado {
  try {
    const crudo = localStorage.getItem(LLAVE);
    return crudo ? (JSON.parse(crudo) as Guardado) : {};
  } catch {
    return {};
  }
}

function escribir(valor: Guardado) {
  try {
    localStorage.setItem(LLAVE, JSON.stringify(valor));
  } catch {
    /* modo privado del navegador: seguimos solo en memoria */
  }
}

type EstadoSucursal = {
  /** Lo elegido para cada negocio: `{ [tenantId]: locationId }`. */
  porNegocio: Guardado;

  /** La sucursal elegida para ese negocio, o `null` si nunca se eligió. */
  elegida: (tenantId: string | null | undefined) => string | null;

  poner: (tenantId: string, locationId: string | null) => void;
};

export const useSucursal = create<EstadoSucursal>((set, get) => ({
  porNegocio: leer(),

  elegida: (tenantId) => {
    if (!tenantId) return null;
    return get().porNegocio[tenantId] ?? null;
  },

  poner: (tenantId, locationId) => {
    const nuevo = { ...get().porNegocio };
    if (locationId === null) {
      delete nuevo[tenantId];
    } else {
      nuevo[tenantId] = locationId;
    }
    escribir(nuevo);
    set({ porNegocio: nuevo });
  },
}));

/**
 * La sucursal que hay que mandar al vender.
 *
 * Devuelve `null` cuando no se eligió ninguna, y eso es correcto: el servidor
 * usa entonces la principal del negocio. Es lo que hace que un negocio de un
 * solo local no tenga que configurar nada.
 */
export function sucursalParaVender(tenantId: string | null | undefined): string | null {
  return useSucursal.getState().elegida(tenantId);
}
